/**
 * Trunnion AI - About Section
 * Version, build info, and backend health.
 */

import { useQuery } from '@tanstack/react-query';
import apiClient from '@/api/client';
import { Card } from '@/components/ui/Card';
import { StatusDot } from '@/components/ui/StatusDot';
import { Shield } from 'lucide-react';

export function AboutSection() {
  const { data: health } = useQuery({
    queryKey: ['health'],
    queryFn: async () => {
      const { data } = await apiClient.get('/health');
      return data;
    },
    refetchInterval: 30000,
  });

  return (
    <div className="space-y-6">
      <Card>
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-trunnion-gold-500/10">
              <Shield className="h-6 w-6 text-trunnion-gold-500" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">
                Trunnion <span className="text-trunnion-gold-500">AI</span>
              </h3>
              <p className="text-xs text-trunnion-slate-400">
                Declarative Agentic Framework
              </p>
            </div>
          </div>

          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-trunnion-slate-400">Version</span>
              <span className="text-white font-mono">2.0.0</span>
            </div>
            <div className="flex justify-between">
              <span className="text-trunnion-slate-400">Build</span>
              <span className="text-white font-mono">2026.02.22</span>
            </div>
            <div className="flex justify-between">
              <span className="text-trunnion-slate-400">API Version</span>
              <span className="text-white font-mono">v1</span>
            </div>
          </div>
        </div>
      </Card>

      <Card>
        <div className="p-6">
          <h3 className="text-lg font-semibold text-white mb-4">System Health</h3>
          <div className="space-y-3">
            <HealthRow
              label="API Server"
              status={health ? 'healthy' : 'unknown'}
            />
            <HealthRow
              label="Database"
              status={health?.database ?? 'unknown'}
            />
            <HealthRow
              label="Redis"
              status={health?.redis ?? 'unknown'}
            />
            <HealthRow
              label="Celery Workers"
              status={health?.celery ?? 'unknown'}
            />
          </div>
        </div>
      </Card>

      <Card>
        <div className="p-6">
          <h3 className="text-lg font-semibold text-white mb-2">License</h3>
          <p className="text-sm text-trunnion-slate-400">
            Trunnion AI DAF Platform. For authorized use only.
            All access is monitored and audited.
          </p>
        </div>
      </Card>
    </div>
  );
}

function HealthRow({ label, status }: { label: string; status: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-trunnion-slate-300">{label}</span>
      <div className="flex items-center gap-2">
        <StatusDot status={status === 'healthy' || status === 'ok' ? 'healthy' : status === 'unknown' ? 'degraded' : 'unhealthy'} />
        <span className="text-xs text-trunnion-slate-400 capitalize">{status}</span>
      </div>
    </div>
  );
}
